import { Component, OnInit, Inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { RatePurpose, SupplierRating, RateField, SupplierRatingAttachment } from 'app/profile/models/supplier-rating.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SupplierProfileState } from 'app/profile/store/app.reducer';
import { Store } from '@ngrx/store';
import { I18nService } from 'app/shared/services/i18n.service';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { getRatePurpose, getSupplierParts, SelectSupplierContactList, getGeneralSettings } from 'app/profile/store/app.selector';
import { ToolCategory, ToolSubcategory, GeneralSettings } from 'app/profile/models/generic.model';
import { ProfileService } from 'app/profile/services/profile.service';
import { TslOption, TslDialog } from '@tesla/ng-tds';
import { SupplierPart } from 'app/profile/models/masterdata.model';
import { SupplierContactPropertyValue } from 'app/profile/models/supplier.model';
import { debounceTime, startWith, map } from 'rxjs/operators';
import * as FileSaver from 'file-saver';

@Component({
    selector: 'app-rating-form-dialog',
    templateUrl: './rating-form-dialog.component.html',
    styleUrls: ['./rating-form-dialog.component.scss']
})
export class RatingFormDialogComponent implements OnInit {
    viewMode = true;
    settings: GeneralSettings;
    ratePurpose$: Observable<RatePurpose[]>;
    contacts: SupplierContactPropertyValue[];
    filteredContacts$: Observable<SupplierContactPropertyValue[]>;
    categories$: Observable<ToolCategory[]>;
    subcategories: ToolSubcategory[];
    filteredsubcategories$: Observable<ToolSubcategory[]>;
    parts$: Observable<SupplierPart[]>;

    contactControl = new FormControl();
    ratingForm: FormGroup;
    ratingOptions = {
        disabled: false,
        showTicksValues: true,
        stepsArray: [
            { value: 1, legend: "Very poor" },
            { value: 2, legend: "Poor" },
            { value: 3, legend: "Average" },
            { value: 4, legend: "Good" },
            { value: 5, legend: "Excellent" }
        ]
    };

    get category() { return this.ratingForm.get('category'); }
    get subject() { return this.ratingForm.get('subject'); }
    get rate() { return this.ratingForm.get('rate'); }
    get note() { return this.ratingForm.get('note'); }
    get fieldType() { return this.ratingForm.get('fieldType'); }
    get fileSizeLimitMB() { return this.settings.fileSizeLimitMB * 1000000; }

    constructor(
        private fb: FormBuilder,
        private translateService: I18nService,
        private store: Store<SupplierProfileState>,
        private dialog: TslDialog,
        public dialogRef: MatDialogRef<RatingFormDialogComponent>,
        private profileSerice: ProfileService,
        @Inject(MAT_DIALOG_DATA) public data: SupplierRating
    ) {
    }

    ngOnInit() {
        this.loadResources();

        this.viewMode = this.data.id > 0;
        this.ratingOptions.disabled = this.viewMode;
        this.ratingForm = this.fb.group({
            category: [{ value: this.data.ratePurposeId, disabled: this.viewMode }, Validators.required],
            subject: [{ value: this.data.subject, disabled: this.viewMode }, Validators.required],
            rate: [{ value: this.data.rate, disabled: this.viewMode }, Validators.required],
            note: [{ value: this.data.note, disabled: this.viewMode }, Validators.required],
            fieldType: [{ value: '', disabled: this.viewMode }]
        });

        this.filteredContacts$ = this.contactControl.valueChanges
            .pipe(
                debounceTime(300),
                startWith(' '),
                map(value => this._filter(value))
            );
    }

    onClose(): void {
        this.dialogRef.close();
    }

    onSave(): void {
        if (this.ratingForm.invalid) {
            return;
        }

        this.data.ratePurposeId = this.category.value;
        this.data.subject = this.subject.value;
        this.data.rate = this.rate.value;
        this.data.note = this.note.value;
        this.dialogRef.close(this.data);
    }

    onFieldChange(option: TslOption): void {
        switch (this.fieldType.value) {
            case '1':
                const subcategorySel: ToolSubcategory = option.value;
                this.data.rateFields.push({
                    externalId: subcategorySel.id,
                    externalValue: subcategorySel.subcategory,
                    fieldOrigin: this.fieldType.value
                });
                break;
            case '2':
                const contactSel: SupplierContactPropertyValue = option.value;
                this.data.rateFields.push({
                    externalId: contactSel.contactId.newValue,
                    externalValue: contactSel.email.newValue,
                    fieldOrigin: this.fieldType.value
                });
                break;
            case '3':
                const partSel: SupplierPart = option.value;
                this.data.rateFields.push({
                    externalId: partSel.partID,
                    externalValue: partSel.partNumber,
                    fieldOrigin: this.fieldType.value
                });
                break;
            default: break;
        }
        this.fieldType.setValue(0);
    }

    onToolCategoryChange(option: TslOption): void {
        const category: ToolCategory = option.value;
        this.filteredsubcategories$ = of(this.subcategories.filter(s => s.toolCategoryId === category.id));
    }

    onRemoveTag(field: RateField): void {
        const index = this.data.rateFields.indexOf(field);
        if (index > -1) {
            this.data.rateFields.splice(index, 1);
            this.contactControl.setValue('');
        }
    }

    onAddFile(file: File): void {
        if (file == null) {
            return;
        }

        if (file.size > this.fileSizeLimitMB) {
            this.dialog.alert(this.translateService.get('Alert'), this.translateService.get('File size can not be more than 10MB!'));
            return;
        }

        this.data.newFiles.push(file);
    }

    onDeleteFile(file: File): void {
        const fileIndex = this.data.newFiles.indexOf(file);
        this.data.newFiles.splice(fileIndex, 1);
    }

    onDownloadAttachment(attach: SupplierRatingAttachment): void {
        this.profileSerice.downloadGenericAttachment(this.data.supplierCode, attach.storageKey)
            .subscribe(file => {
                FileSaver.saveAs(file, attach.fileName);
            });
    }

    getFileType(fileName: string): string {
        const nameArray = fileName.split(".");
        return nameArray[nameArray.length - 1];
    }

    private _filter(value: string): any {
        if (value && this.contacts) {
            const filterValue = value.toLowerCase();
            return this.contacts.filter(c =>
                c.firstName.newValue.toLowerCase().includes(filterValue) ||
                c.lastName.newValue.toLowerCase().includes(filterValue) ||
                c.email.newValue.toLowerCase().includes(filterValue));
        }
        else {
            return [];
        }
    }

    private loadResources(): void {
        this.store.select(getGeneralSettings).subscribe(res => this.settings = res);
        this.ratePurpose$ = this.store.select(getRatePurpose);
        this.parts$ = this.store.select(getSupplierParts);
        this.store.select(SelectSupplierContactList).subscribe(res => this.contacts = res);
        this.categories$ = this.profileSerice.getCategories();
        this.profileSerice.getSubcategories(this.data.supplierCode, 0).subscribe(res => this.subcategories = res);
    }
}
